'use client';

import { WarningIcon } from '@phosphor-icons/react';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import type { GoalsOverview } from '@/types';
import { goalStatus } from './helpers';

interface Props {
  overview: GoalsOverview;
}

export function PlannerSummary({ overview }: Props) {
  const formatCurrency = useFormatCurrency();
  const totalCommitted = overview.totalCommitted;
  const availableCapacity = overview.availableCapacity;
  const overCommitted = totalCommitted > availableCapacity && availableCapacity > 0;
  const inAnyTrouble = overview.goals.filter((g) => {
    const s = goalStatus(g);
    return s === 'behind' || s === 'stalled' || s === 'tight';
  }).length;

  const totalSaved = overview.totalAccumulated;
  const totalTarget = overview.totalPlanned;
  const overallPct = overview.overallPercent;
  const remaining = Math.max(0, totalTarget - totalSaved);

  return (
    <div className="grid grid-cols-12 gap-3">
      <div className="col-span-12 rounded-[var(--radius-3xl)] border border-border bg-surface shadow-[var(--shadow-1)] p-4 lg:col-span-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="font-mono text-3xs font-semibold tracking-[0.1em] uppercase text-text-tertiary">Progresso geral</div>
          <div className="text-2xs text-text-tertiary">
            {overview.goals.length} {overview.goals.length === 1 ? 'meta' : 'metas'}
          </div>
        </div>
        <div className="mb-1 flex flex-wrap items-baseline gap-2">
          <div className="font-sans text-3xl leading-none font-bold tracking-tight text-success-text tabular-nums md:text-4xl">
            {overallPct}%
          </div>
          <div className="font-mono text-xs text-text-tertiary">
            {formatCurrency(totalSaved)} <span className="text-text-tertiary">/</span> {formatCurrency(totalTarget)}
          </div>
        </div>
        <div className="mt-1 mb-3 text-xs text-text-tertiary">
          {inAnyTrouble === 0
            ? 'todas as metas no ritmo planejado'
            : `${inAnyTrouble} ${inAnyTrouble === 1 ? 'meta precisa' : 'metas precisam'} de atenção`}
        </div>
        <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-divider">
          <div
            className="h-full rounded-full bg-success transition-all"
            style={{ width: `${Math.min(100, overallPct)}%` }}
          />
        </div>
      </div>

      <div className="col-span-12 flex flex-col justify-between gap-2 rounded-[var(--radius-3xl)] border border-border bg-surface shadow-[var(--shadow-1)] p-4 sm:col-span-6 lg:col-span-4">
        <div className="flex items-center justify-between">
          <div className="font-mono text-3xs font-semibold tracking-[0.1em] uppercase text-text-tertiary">Comprometimento mensal</div>
          {overCommitted && (
            <span className="flex items-center gap-1 text-2xs font-medium text-error-text">
              <WarningIcon size={10} weight="bold" />
              acima do limite
            </span>
          )}
        </div>
        <div>
          <div
            className={`font-sans tabular-nums tracking-tight leading-none text-xl md:text-2xl font-bold ${
              overCommitted ? 'text-error-text' : 'text-text-primary'
            }`}
          >
            {formatCurrency(totalCommitted)}
          </div>
          <div className="mt-1.5 font-mono text-2xs text-text-tertiary">
            de {formatCurrency(availableCapacity)} disponível
          </div>
        </div>
        <div className="h-1 w-full overflow-hidden rounded-full bg-divider">
          <div
            className="h-full transition-all"
            style={{
              width: `${availableCapacity > 0 ? Math.min(100, (totalCommitted / availableCapacity) * 100) : 0}%`,
              backgroundColor: overCommitted ? 'var(--error)' : 'var(--success)',
            }}
          />
        </div>
      </div>

      <div className="col-span-12 flex flex-col justify-between gap-2 rounded-[var(--radius-3xl)] border border-border bg-surface shadow-[var(--shadow-1)] p-4 sm:col-span-6 lg:col-span-3">
        <div className="font-mono text-3xs font-semibold tracking-[0.1em] uppercase text-text-tertiary">Em construção</div>
        <div>
          <div className="font-sans text-xl leading-none font-bold tracking-tight text-warning-text tabular-nums md:text-2xl">
            {formatCurrency(remaining)}
          </div>
          <div className="mt-1.5 text-2xs text-text-tertiary">faltam para concluir todas</div>
        </div>
      </div>
    </div>
  );
}
